import { useEffect, useRef, useState } from 'react';
import '../../../styles/pages/tara/chat/taraChat.scss';
import TaraChatItemAI from './TaraChatItemAI';
import TaraChatItemUser from './TaraChatItemUser';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../store/store';
import get_tara_single_chat_api_call from '../../../lib/api/tara/get_tara_single_chat_api_call';
import { useNavigate } from 'react-router-dom';
import {
  setCurrentChat,
  setIsBotTyping,
} from '../../../store/reducers/chatSlice';
import LoadingSpinner from '../../../ui/loadingSpinner/LoadingSpinner';
import generateRandomString from '../../../lib/helper-functions/generateRandomString';

type Props = {
  conversationId: string | undefined;
  isStreaming?: boolean;
};

const TaraChat = ({ conversationId, isStreaming }: Props) => {
  const currentChat = useSelector((state: RootState) => state.chat.currentChat);
  const isBotTypingGlobal = useSelector(
    (state: RootState) => state.chat.isBotTyping
  );
  const isBotTyping = isStreaming ?? isBotTypingGlobal;
  const chatContainerRef = useRef<HTMLDivElement | null>(null);
  const [loading, setLoading] = useState<'pending' | 'success' | 'error'>(
    'pending'
  );
  const navigate = useNavigate();
  const dispatch = useDispatch();

  useEffect(() => {
    // Auto-scroll to bottom on new messages or typing state change
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTo({
        top: chatContainerRef.current.scrollHeight,
        behavior: 'smooth',
      });
    }
  }, [currentChat?.chatMessages, isBotTyping]);

  useEffect(() => {
    if (!conversationId || conversationId.startsWith('temp_')) {
      // Local or temporary chat – skip loading from backend
      setLoading('success');
      return;
    }

    setLoading('pending');

    get_tara_single_chat_api_call(conversationId)
      .then((res) => {
        if (!res || !res.chat || !Array.isArray(res.chat)) {
          throw new Error('Invalid response structure');
        }

        const messages = res.chat.flatMap((entry: any) => {
          const role = entry?.role === 'user' ? 'user' : 'ai';
          const responseId = entry?.response_id || null;
          const feedbackReaction = entry?.feedback_reaction ?? null;

          // ✅ Case 1: entry.content is an array (standard multi-part format)
          if (Array.isArray(entry?.content)) {
            return entry.content.map((item: any, index: number) => {
              const messageType: 'text' | 'chart' | 'table' =
                item?.type === 'chart'
                  ? 'chart'
                  : item?.type === 'table'
                  ? 'table'
                  : 'text';

              if (item?.type === 'table' && item?.table) {
                return {
                  id: item?.id ?? generateRandomString(10),
                  message: item.table,
                  messageType: 'table',
                  role,
                  responseId:
                    index === entry.content.length - 1 ? responseId : null,
                  feedbackReaction:
                    index === entry.content.length - 1
                      ? feedbackReaction
                      : null,
                };
              }

              // ✅ Ensure message is always a string to prevent crashing React
              const safeMessage =
                typeof item?.content === 'string'
                  ? item.content
                  : typeof item?.content === 'object' && item?.content !== null
                  ? JSON.stringify(item.content, null, 2)
                  : '';

              return {
                id: item?.id ?? generateRandomString(10),
                message: safeMessage,
                messageType,
                role,
                responseId:
                  index === entry.content.length - 1 ? responseId : null,
                feedbackReaction:
                  index === entry.content.length - 1 ? feedbackReaction : null,
              };
            });
          }

          // ✅ Case 2: entry.content is a plain string
          if (typeof entry?.content === 'string') {
            return [
              {
                id: generateRandomString(10),
                message: entry.content,
                messageType: 'text',
                role,
                responseId,
                feedbackReaction,
              },
            ];
          }

          // ✅ Case 3: entry.content is an object (non-array, non-string)
          if (typeof entry?.content === 'object' && entry?.content !== null) {
            return [
              {
                id: generateRandomString(10),
                message: JSON.stringify(entry.content, null, 2),
                messageType: 'text',
                role,
                responseId,
                feedbackReaction,
              },
            ];
          }

          // ❌ Case 4: unknown format – skip
          return [];
        });

        const newChat = {
          chatId: res.conversation_id ?? conversationId,
          chatStartedDate: new Date().toISOString(),
          chatLastUpdatedDate: new Date().toISOString(),
          chatMessages: messages,
        };

        dispatch(setCurrentChat(newChat));

        // Navigate to real conversation ID if different from URL
        if (conversationId !== res.conversation_id) {
          navigate(`/tara/chat/${res.conversation_id}`, { replace: true });
        }

        setLoading('success');
      })
      .catch((error) => {
        console.error('Error fetching tara chat:', error);
        setLoading('error');
        dispatch(setCurrentChat(null));
        dispatch(setIsBotTyping(false));
        navigate('/tara');
      });
  }, [conversationId]);

  return (
    <div className='taraChat' ref={chatContainerRef}>
      {loading === 'pending' && <LoadingSpinner />}
      {loading === 'success' &&
        currentChat &&
        currentChat.chatMessages?.length > 0 && (
          <div className='taraChat__content'>
            {currentChat.chatMessages.map((chatMessage, i) => {
              const message =
                chatMessage.messageType === 'table'
                  ? chatMessage.message
                  : typeof chatMessage?.message === 'string'
                  ? chatMessage.message
                  : JSON.stringify(chatMessage?.message ?? '', null, 2);

              if (chatMessage.role === 'user') {
                const remainingMessages = currentChat.chatMessages.slice(i + 1);
                const nextAIMessageWithResponseId = remainingMessages.find(
                  (msg) => msg.role === 'ai' && msg.responseId
                );
                const responseId =
                  nextAIMessageWithResponseId?.responseId || null;
                const isTempChat = currentChat.chatId
                  ?.toString()
                  .startsWith('temp_');

                const isLastUserMessage =
                  currentChat.chatMessages
                    .slice(i + 1)
                    .findIndex((msg) => msg.role === 'user') === -1;

                const isCurrentlyStreaming = isBotTyping && isLastUserMessage;

                const showSaveButton = !!(
                  responseId &&
                  currentChat.chatId &&
                  !isTempChat &&
                  !isCurrentlyStreaming
                );

                return (
                  <TaraChatItemUser
                    key={`${chatMessage.id}-${i}`}
                    message={message}
                    messageType={chatMessage.messageType ?? 'text'}
                    conversationId={currentChat.chatId?.toString() || null}
                    responseId={responseId}
                    showSaveButton={showSaveButton}
                  />
                );
              } else {
                const currentMessageResponseId = chatMessage.responseId;
                const responseIdForChart =
                  chatMessage.messageType === 'chart' &&
                  !currentMessageResponseId
                    ? currentChat.chatMessages
                        .slice(i)
                        .find((msg) => msg.role === 'ai' && msg.responseId)
                        ?.responseId || null
                    : currentMessageResponseId;

                return (
                  <TaraChatItemAI
                    key={`${chatMessage.id}-${i}`}
                    message={message}
                    messageType={chatMessage.messageType ?? 'text'}
                    isTyping={false}
                    debugInfo={chatMessage.debugInfo ?? null}
                    chatIndex={i}
                    chatMessages={currentChat?.chatMessages ?? []}
                    responseId={responseIdForChart}
                    feedbackReaction={chatMessage.feedbackReaction ?? null}
                    conversationId={currentChat.chatId?.toString() || null}
                  />
                );
              }
            })}
            {isBotTyping && (
              <TaraChatItemAI
                key='typing-indicator'
                message=''
                messageType='text'
                isTyping={true}
              />
            )}
          </div>
        )}
    </div>
  );
};

export default TaraChat;
